import { useOrganizationId } from 'application/contexts/SiteContext';
import { usePageContext } from 'application/contexts/PageContext';
import { imageToJsonLd, findPageHeaderHero, deriveAreaServed } from 'helpers/jsonLd';
import { JsonLdScript } from './JsonLdScript';

type ContentPageProperties = {
	description?: string;
	pageHeader?: Content.HeroCard[];
	pageCategory?: 'Service' | 'ServiceOverview' | '';
};

// Rendered only via PageSchemaJsonLd's dispatch.
export const ContentPageJsonLd: React.FC = () => {
	const pageContext = usePageContext();
	const organizationId = useOrganizationId();

	const properties = pageContext.content as unknown as ContentPageProperties;
	const hero = findPageHeaderHero(properties?.pageHeader);
	const name = hero?.heading || pageContext.name;
	if (!name) return null;

	const image = imageToJsonLd(hero?.featuredImage || hero?.image);

	// inLanguage doesn't exist on Service (extends Intangible, not CreativeWork) - added per-branch below.
	const shared = {
		'@context': 'https://schema.org',
		name,
		description: properties?.description || undefined,
		url: pageContext.path,
		image,
	};

	const page =
		properties?.pageCategory === 'Service'
			? {
					...shared,
					'@type': 'Service',
					provider: organizationId ? { '@id': organizationId } : undefined,
					areaServed: deriveAreaServed(pageContext.culture),
			  }
			: properties?.pageCategory === 'ServiceOverview'
			? { ...shared, '@type': 'CollectionPage', inLanguage: pageContext.culture || undefined }
			: {
					...shared,
					'@type': 'CreativeWork',
					inLanguage: pageContext.culture || undefined,
					datePublished: pageContext.createDate || undefined,
					dateModified: pageContext.updateDate || undefined,
			  };

	return <JsonLdScript id="content-page-jsonld" data={page} />;
};
